/**
 * Secure API Client Composable
 *
 * This composable provides a wrapper around Nuxt's $fetch utility
 * to standardize API calls across the application.
 *
 * Features:
 * - Automatically prefixes requests with the API URL from runtime config
 * - Uses the global auth interceptor for token expiration handling
 * - Includes credentials (cookies) for authentication
 * - Centralizes API configuration
 * - Handles 401 retry success responses transparently
 *
 * Usage:
 * ```typescript
 * const securedApi = useSecuredApi()
 *
 * // GET request
 * const data = await securedApi('/auth/user')
 *
 * // POST request
 * const result = await securedApi('/auth/login', {
 *   method: 'POST',
 *   body: { username, password }
 * })
 * ```
 *
 * Important:
 * - Always use endpoint paths (e.g., '/auth/user'), not full URLs
 * - The API base URL is configured via NUXT_API_URL environment variable
 * - Credentials are automatically included for cookie-based auth
 * - Token expiration is automatically checked and refresh triggered when needed
 * - 401 responses trigger automatic token refresh and request retry
 */

import type { FetchOptions } from 'ofetch'
import { AuthRetrySuccessError } from '~/types/auth'

type SecuredApiFetchOptions = FetchOptions & {
  method?:
    | 'GET'
    | 'HEAD'
    | 'PATCH'
    | 'POST'
    | 'PUT'
    | 'DELETE'
    | 'CONNECT'
    | 'OPTIONS'
    | 'TRACE'
  /** When true, the auth interceptor will not handle 401 errors for this request */
  _skipAuthRetry?: boolean
}

/**
 * Creates a secured API client that automatically handles:
 * - API URL prefixing from runtime configuration
 * - Cookie-based authentication via credentials: 'include'
 * - Token expiration checking and refresh via global interceptor
 * - Transparent 401 retry with token refresh (returns successful retry result)
 *
 * @returns A function that wraps $fetch with proper configuration
 */
export function useSecuredApi() {
  const config = useRuntimeConfig()
  const apiUrl = config.public.apiUrl
  const nuxtApp = useNuxtApp()

  /**
   * Makes an authenticated API request
   *
   * @param endpoint - The API endpoint path (e.g., '/auth/user')
   * @param options - Additional fetch options
   * @returns Promise with the response data
   */
  return async <T = unknown>(
    endpoint: string,
    options?: SecuredApiFetchOptions
  ): Promise<T> => {
    // Ensure endpoint starts with /
    const normalizedEndpoint = endpoint.startsWith('/')
      ? endpoint
      : `/${endpoint}`

    // Use the secured fetch from the plugin if available (includes interceptor)
    // Falls back to regular $fetch if plugin not yet loaded
    const fetchFn = nuxtApp.$securedFetch || $fetch

    try {
      return await fetchFn<T>(`${apiUrl}${normalizedEndpoint}`, {
        credentials: 'include', // Include cookies for authentication
        ...options,
      })
    } catch (error) {
      // Handle successful 401 retry - return the retry result as success
      if (error instanceof AuthRetrySuccessError) {
        return error.data as T
      }
      // Re-throw all other errors
      throw error
    }
  }
}
